import React, { useState } from 'react';
import { FiUser, FiCreditCard, FiPhone, FiAlertCircle } from 'react-icons/fi';
import api from '../services/api';
import './PlayerRegisterForm.css';

/**
 * 玩家報名表單元件
 * 高端玻璃擬態卡片設計
 */
function PlayerRegisterForm({ onSuccess, termsContent }) {
  const [form, setForm] = useState({
    name: '',
    nationalId: '',
    phone: '',
    agreedTerms: false,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState('');
  const [showTerms, setShowTerms] = useState(false);

  const validateField = (field, value) => {
    switch (field) {
      case 'name':
        return value.trim() ? '' : '請輸入姓名';
      case 'nationalId': {
        if (!value.trim()) return '請輸入身分證字號';
        const re = /^[A-Za-z][12]\d{8}$/;
        return re.test(value) ? '' : '身分證字號格式不正確';
      }
      case 'phone': {
        if (!value.trim()) return '請輸入手機號碼';
        return /^09\d{8}$/.test(value) ? '' : '手機號碼格式不正確（09開頭共10碼）';
      }
      case 'agreedTerms':
        return value ? '' : '請同意活動條款';
      default:
        return '';
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: validateField(field, value) }));
    }
  };

  const handleBlur = (field) => {
    setErrors(prev => ({
      ...prev,
      [field]: validateField(field, form[field]),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError('');

    // 全欄位驗證
    const newErrors = {};
    Object.keys(form).forEach(field => {
      const err = validateField(field, form[field]);
      if (err) newErrors[field] = err;
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        name: form.name.trim(),
        nationalId: form.nationalId.toUpperCase().trim(),
        phone: form.phone.trim(),
        agreedTerms: form.agreedTerms,
      };
      const result = await api.register(submitData);
      onSuccess(result, submitData);
    } catch (err) {
      if (err.data?.errors) {
        const fieldErrors = {};
        err.data.errors.forEach(e => { fieldErrors[e.field] = e.message; });
        setErrors(fieldErrors);
      } else {
        setServerError(err.message || '報名失敗，請稍後再試');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-form-container">
      <form className="glass-card register-form" onSubmit={handleSubmit} noValidate>
        <div className="form-header">
          <h2 className="form-title">立即參加抽獎</h2>
          <p className="form-desc">填寫以下資料即可參與幸運輪盤</p>
        </div>

        {serverError && (
          <div className="form-server-error">{serverError}</div>
        )}

        <div className="form-group">
          <label className="form-label" htmlFor="name">
            <FiUser className="label-icon-svg" /> 姓名
          </label>
          <input
            id="name"
            type="text"
            className={`input-field ${errors.name ? 'error' : ''}`}
            placeholder="請輸入真實姓名"
            value={form.name}
            onChange={e => handleChange('name', e.target.value)}
            onBlur={() => handleBlur('name')}
            disabled={loading}
            maxLength={50}
            autoComplete="name"
          />
          {errors.name && <div className="error-text"><FiAlertCircle className="err-icon" /> {errors.name}</div>}
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="nationalId">
            <FiCreditCard className="label-icon-svg" /> 身分證字號
          </label>
          <input
            id="nationalId"
            type="text"
            className={`input-field ${errors.nationalId ? 'error' : ''}`}
            placeholder="例：A123456789"
            value={form.nationalId}
            onChange={e => handleChange('nationalId', e.target.value.toUpperCase())}
            onBlur={() => handleBlur('nationalId')}
            disabled={loading}
            maxLength={10}
            autoComplete="off"
          />
          {errors.nationalId && <div className="error-text"><FiAlertCircle className="err-icon" /> {errors.nationalId}</div>}
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="phone">
            <FiPhone className="label-icon-svg" /> 手機號碼
          </label>
          <input
            id="phone"
            type="tel"
            className={`input-field ${errors.phone ? 'error' : ''}`}
            placeholder="例：0912345678"
            value={form.phone}
            onChange={e => handleChange('phone', e.target.value.replace(/\D/g, ''))}
            onBlur={() => handleBlur('phone')}
            disabled={loading}
            maxLength={10}
            autoComplete="tel"
          />
          {errors.phone && <div className="error-text"><FiAlertCircle className="err-icon" /> {errors.phone}</div>}
        </div>

        <div className="form-group checkbox-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              className="checkbox-input"
              checked={form.agreedTerms}
              onChange={e => handleChange('agreedTerms', e.target.checked)}
              disabled={loading}
            />
            <span className="checkbox-custom" />
            <span className="checkbox-text">
              我已閱讀並同意<button type="button" className="terms-link" onClick={() => setShowTerms(true)}>活動條款與個資使用聲明</button>
            </span>
          </label>
          {errors.agreedTerms && <div className="error-text"><FiAlertCircle className="err-icon" /> {errors.agreedTerms}</div>}
        </div>

        <button type="submit" className="btn-primary submit-btn" disabled={loading}>
          {loading && <div className="spinner" />}
          {loading ? '報名中...' : '立即參加'}
        </button>
      </form>

      {/* 活動條款彈窗 */}
      {showTerms && (
        <div className="terms-overlay" onClick={() => setShowTerms(false)}>
          <div className="terms-modal" onClick={e => e.stopPropagation()}>
            <div className="terms-modal-header">
              <h3>活動條款與個資使用聲明</h3>
              <button className="terms-close-btn" onClick={() => setShowTerms(false)}>&times;</button>
            </div>
            <div className="terms-modal-body">
              {termsContent ? (
                <div className="terms-text" dangerouslySetInnerHTML={{ __html: termsContent.replace(/\n/g, '<br/>') }} />
              ) : (
                <div className="terms-text terms-default">
                  <p><strong>一、活動辦法</strong></p>
                  <p>本活動由主辦單位舉辦，參加者須填寫真實個人資料方可參與抽獎。</p>
                  <br/>
                  <p><strong>二、個人資料蒐集與使用</strong></p>
                  <p>您提供之姓名、身分證字號及手機號碼，僅供本次活動身分驗證、中獎通知及獎品發放之用途。活動結束後將依法妥善處理。</p>
                  <br/>
                  <p><strong>三、注意事項</strong></p>
                  <p>每人限參加一次，重複報名以首次為準。主辦單位保留活動修改、暫停或終止之權利。</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PlayerRegisterForm;
